// Define the interface here instead of importing from component
export interface NotificationOptions {
  id: string;
  message: string;
  title?: string;
  htmlMessage?: string;
  type?: 'success' | 'warning' | 'error' | 'info';
  duration?: number;
  showProgress?: boolean;
  progress?: number;
  onClick?: (() => void) | string;
  linkText?: string;
  linkRoute?: string;
  autoCloseOnClick?: boolean;
}

type NotificationInstance = {
  showNotification: (options: Omit<NotificationOptions, 'id' | 'progress'>) => string;
  removeNotification: (id: string) => void;
  clearAll: () => void;
};

// 创建一个全局变量来存储通知实例的方法
let notificationInstance: NotificationInstance | null = null;

// 设置通知实例
export const setNotificationInstance = (instance: NotificationInstance | null) => {
  notificationInstance = instance;
};

// 显示通知
export const showNotification = (options: Omit<NotificationOptions, 'id' | 'progress'>) => {
  if (notificationInstance) {
    return notificationInstance.showNotification(options);
  }
  console.warn('Notification instance not initialized');
  return '';
};

// 移除通知
export const removeNotification = (id: string) => {
  if (notificationInstance) {
    notificationInstance.removeNotification(id);
  }
};

// 清除所有通知
export const clearAll = () => {
  if (notificationInstance) {
    notificationInstance.clearAll();
  }
};

// 导出便捷方法
export const notification = {
  show: showNotification,
  success: (message: string, options: Partial<Omit<NotificationOptions, 'id' | 'progress' | 'message' | 'type'>> = {}) => {
    return showNotification({ message, type: 'success', ...options });
  },
  warning: (message: string, options: Partial<Omit<NotificationOptions, 'id' | 'progress' | 'message' | 'type'>> = {}) => {
    return showNotification({ message, type: 'warning', ...options });
  },
  error: (message: string, options: Partial<Omit<NotificationOptions, 'id' | 'progress' | 'message' | 'type'>> = {}) => {
    return showNotification({ message, type: 'error', ...options });
  },
  info: (message: string, options: Partial<Omit<NotificationOptions, 'id' | 'progress' | 'message' | 'type'>> = {}) => {
    return showNotification({ message, type: 'info', ...options });
  },
  close: removeNotification,
  closeAll: clearAll
};

// 挂载到 window 对象上
declare global {
  interface Window {
    $notify: typeof notification;
    $removeNotify: typeof removeNotification;
    $clearAllNotify: typeof clearAll;
  }
}

if (typeof window !== 'undefined') {
  window.$notify = notification;
  window.$removeNotify = removeNotification;
  window.$clearAllNotify = clearAll;
}